import { useState } from 'react'
import { Download, CheckSquare, Square, X } from 'lucide-react'
import type { UiPhoto } from '@/data/drops'
import { PhotoLightbox } from './PhotoLightbox'
import { UPLOAD_WORKER_URL } from '@/lib/env'
import { getSupabaseAuthToken } from '@/lib/supabaseClient'

type Props = {
  photos: UiPhoto[]
  dropId: string
  dropName?: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function statusBadge(status: string) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    processing: 'bg-blue-500/20 text-blue-400',
    complete: 'bg-emerald-500/20 text-emerald-400',
    failed: 'bg-red-500/20 text-red-400',
    skipped: 'bg-zinc-500/20 text-zinc-400',
  }
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[status] || styles.pending}`}>
      {status}
    </span>
  )
}

export function PhotoGrid({ photos, dropId, dropName }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [downloading, setDownloading] = useState(false)

  const selectionMode = selected.size > 0

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAll() {
    setSelected(new Set(photos.map(p => p.id)))
  }

  function clearSelection() {
    setSelected(new Set())
  }

  async function downloadZip(photoUrls?: string[]) {
    if (!UPLOAD_WORKER_URL) return
    setDownloading(true)
    try {
      const token = await getSupabaseAuthToken()
      const body: { dropId: string; photoKeys?: string[]; dropName?: string } = { dropId, dropName }
      if (photoUrls) body.photoKeys = photoUrls
      const res = await fetch(`${UPLOAD_WORKER_URL}/download-zip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'photos.zip'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  function downloadSelected() {
    const urls = photos.filter(p => selected.has(p.id)).map(p => p.originalUrl)
    downloadZip(urls)
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-2">
        {selectionMode ? (
          <>
            <span className="text-sm text-muted-foreground">{selected.size} selected</span>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs px-3 py-1.5 rounded-md border hover:bg-accent">Select all</button>
              <button onClick={clearSelection} className="text-xs px-3 py-1.5 rounded-md border hover:bg-accent">
                <X className="h-3 w-3 inline mr-1" />Clear
              </button>
              <button
                onClick={downloadSelected}
                disabled={downloading}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Download className="h-3 w-3 inline mr-1" />
                {downloading ? 'Zipping...' : `Download selected (${selected.size})`}
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="text-sm text-muted-foreground">{photos.length} photos</span>
            <button
              onClick={() => downloadZip()}
              disabled={downloading || photos.length === 0}
              className="text-xs px-3 py-1.5 rounded-md border hover:bg-accent disabled:opacity-50"
            >
              <Download className="h-3 w-3 inline mr-1" />
              {downloading ? 'Zipping...' : 'Download all'}
            </button>
          </>
        )}
      </div>

      {/* Masonry grid */}
      <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
        {photos.map((photo, idx) => {
          const isSelected = selected.has(photo.id)
          const filename = photo.originalUrl.split('/').pop() || 'photo'
          return (
            <div
              key={photo.id}
              className="break-inside-avoid relative group cursor-pointer rounded-lg overflow-hidden bg-card border"
              onClick={() => setLightboxIndex(idx)}
            >
              <img
                src={photo.thumbnailUrl || photo.originalUrl}
                alt={photo.caption || filename}
                className="w-full block"
                loading="lazy"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                <div className="flex justify-between items-start">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(photo.id) }}
                    className="text-white"
                  >
                    {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
                  </button>
                  {statusBadge(photo.processingStatus)}
                </div>
                <div className="text-white text-xs space-y-0.5">
                  <p className="truncate font-medium">{filename}</p>
                  {photo.width && photo.height && <p>{photo.width} x {photo.height}</p>}
                  <p>{formatBytes(photo.fileSizeBytes)}</p>
                </div>
              </div>
              {/* Selection indicator when not hovering */}
              {isSelected && (
                <div className="absolute top-2 left-2">
                  <CheckSquare className="h-5 w-5 text-primary" />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}
